import { useEffect, useMemo, useState } from "react";
import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import { z } from "zod";
import { AuthContext, type AuthContextValue, type SocialProvider } from "./authContext";

const publicConfigSchema = z.object({
  supabaseUrl: z.url(),
  supabasePublishableKey: z.string().min(20),
  socialProviders: z.object({ google: z.boolean(), facebook: z.boolean() })
});

export function AuthProvider({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  const [client, setClient] = useState<SupabaseClient | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [socialProviders, setSocialProviders] = useState<Readonly<Record<SocialProvider, boolean>>>({ google: false, facebook: false });

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;
    void fetch("/api/config", { headers: { accept: "application/json" } })
      .then(async (response) => {
        if (!response.ok) throw new Error("인증 서비스를 불러오지 못했습니다.");
        const config = publicConfigSchema.parse(await response.json());
        const configuredClient = createClient(config.supabaseUrl, config.supabasePublishableKey, {
          auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, flowType: "pkce" }
        });
        const currentSession = (await configuredClient.auth.getSession()).data.session;
        if (!active) return;
        setClient(configuredClient);
        setSession(currentSession);
        setSocialProviders(config.socialProviders);
        const listener = configuredClient.auth.onAuthStateChange((_event, nextSession) => { setSession(nextSession); });
        unsubscribe = () => { listener.data.subscription.unsubscribe(); };
      })
      .catch((cause: unknown) => {
        if (active) setError(cause instanceof Error ? cause.message : "인증 서비스를 불러오지 못했습니다.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; unsubscribe?.(); };
  }, []);

  const value = useMemo<AuthContextValue>(() => ({ client, session, loading, error, socialProviders }), [client, session, loading, error, socialProviders]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
